import type { Vec3 } from '../types';

export interface MarkPoiFormResult {
  name: string;
  category: string;
  floor: number;
  description?: string;
}

/**
 * Small bottom-sheet shown after tapping "Mark POI here" (main.ts). The
 * position is captured by the caller (current localized map-space pose)
 * before this form even opens — this just collects the human-entered
 * metadata (name/category/floor/description) and hands it back.
 */
export class MarkPoiForm {
  readonly el: HTMLDivElement;
  private nameInput: HTMLInputElement;
  private categoryInput: HTMLInputElement;
  private floorInput: HTMLInputElement;
  private descInput: HTMLInputElement;
  private posLabel: HTMLParagraphElement;
  private statusEl: HTMLParagraphElement;
  private saveBtn: HTMLButtonElement;
  private position: Vec3 | null = null;
  private saving = false;

  constructor(private onSave: (result: MarkPoiFormResult, position: Vec3) => Promise<void>) {
    this.el = document.createElement('div');
    this.el.className = 'sheet hidden';
    this.el.innerHTML = `
      <div class="sheet-handle"></div>
      <h2>Mark POI here</h2>
      <p class="muted" data-role="pos"></p>
    `;

    this.posLabel = this.el.querySelector('[data-role="pos"]')!;

    this.nameInput = labeledInput('Name', 'text', 'e.g. Meeting Room B');
    this.categoryInput = labeledInput('Category', 'text', 'e.g. Rooms');
    this.floorInput = labeledInput('Floor', 'number', '0');
    this.floorInput.value = '0';
    this.descInput = labeledInput('Description (optional)', 'text', '');

    this.statusEl = document.createElement('p');
    this.statusEl.className = 'hint hidden';

    this.saveBtn = document.createElement('button');
    this.saveBtn.className = 'btn primary';
    this.saveBtn.textContent = 'Save POI';
    this.saveBtn.addEventListener('click', () => void this.save());

    const cancelBtn = document.createElement('button');
    cancelBtn.className = 'btn ghost';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => this.hide());

    for (const input of [this.nameInput, this.categoryInput, this.floorInput, this.descInput]) {
      this.el.appendChild(input.parentElement!);
    }
    this.el.append(this.statusEl, this.saveBtn, cancelBtn);
  }

  show(position: Vec3, defaultFloor = 0): void {
    this.position = position;
    this.floorInput.value = String(defaultFloor);
    this.posLabel.textContent = `Position: x ${position.x.toFixed(2)}, y ${position.y.toFixed(2)}, z ${position.z.toFixed(2)}`;
    this.nameInput.value = '';
    this.categoryInput.value = '';
    this.descInput.value = '';
    this.statusEl.classList.add('hidden');
    this.el.classList.remove('hidden');
  }

  hide(): void {
    this.el.classList.add('hidden');
  }

  private async save(): Promise<void> {
    if (this.saving || !this.position) return;
    const name = this.nameInput.value.trim();
    const category = this.categoryInput.value.trim();
    const floor = Number(this.floorInput.value) || 0;
    if (!name || !category) {
      this.setStatus('Name and category are required.');
      return;
    }

    this.saving = true;
    this.saveBtn.disabled = true;
    this.setStatus('Saving…');
    try {
      await this.onSave(
        { name, category, floor, description: this.descInput.value.trim() || undefined },
        this.position,
      );
      this.hide();
    } catch (err) {
      this.setStatus(err instanceof Error ? err.message : String(err));
    } finally {
      this.saving = false;
      this.saveBtn.disabled = false;
    }
  }

  private setStatus(text: string): void {
    this.statusEl.textContent = text;
    this.statusEl.classList.remove('hidden');
  }
}

function labeledInput(label: string, type: string, placeholder: string): HTMLInputElement {
  const wrapper = document.createElement('label');
  wrapper.textContent = label;
  const input = document.createElement('input');
  input.type = type;
  input.placeholder = placeholder;
  input.className = 'search-input';
  wrapper.appendChild(input);
  return input;
}
